import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Page, KeyVal } from 'equasion-lib';
import ZonePickerModal, {
  describeUnits,
  useZoneScanOnLoad
} from '@/lib/shared-coms/ZonePickerModal';
import ActionTopBar from '@/lib/shared-coms/ActionTopBar';
import { FiArrowDown, FiCheckCircle, FiEdit } from 'react-icons/fi';
import ProductInLocation from '@/lib/shared-coms/ProductInLocation';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';
import './QuarantineUnserialized.scss';
import { useRootStore } from '@/lib/providers/RootProvider';


const QuarantineUnserialized = ({
  quarantineUnserializedItems,
  getStockZoneInventoryBalances,
  stockZoneInventoryBalances = []
}) => {
  const companyId = useRootStore((state) => state.selectedCompany.id);
  const [quarantineAmounts, setQuarantineAmounts] = useState([]);
  const [modalZoneType, setModalZoneType] = useState(null);
  const navigate = useNavigate();
  const [quarantined, setQuarantined] = useState(false);
  const [stockZoneId, setStockZoneId] = useState(null);
  const [stockZoneName, setStockZoneName] = useState('');
  const [quarantineZoneId, setQuarantineZoneId] = useState(null);
  const [quarantineZoneName, setQuarantineZoneName] = useState('');
  const isQuarantineModal = modalZoneType === 'Quarantine';
  // The screen starts at the stock zone the units come out of, so that is the scanner that opens
  // with it. The quarantine zone opens on a tap, once the quantities are in.
  const { tapPrompt, pickerProps } = useZoneScanOnLoad(
    () => setModalZoneType('Stock'),
    !stockZoneId
  );
  const quarantiningUnits = describeUnits(quarantineAmounts, stockZoneInventoryBalances);

  const submitQuarantineUnserializedItems = async () => {
    try {
      await quarantineUnserializedItems({
        variables: {
          companyId,
          quarantineZoneId,
          quarantineUnserializedItemsAttributes: quarantineAmounts
            .filter(({ quantity }) => quantity > 0)
            .map(({ id, quantity }) => ({
              zoneInventoryBalanceId: id,
              quantity
            }))
        }
      });
      setQuarantined(true);
    } catch (e) {
      console.log(`ERROR QUARANTINING PRODUCTS: ${e}`);
    }
  };

  useEffect(() => {
    if (stockZoneInventoryBalances) {
      setQuarantineAmounts(
        stockZoneInventoryBalances.map(({ id }) => ({
          id,
          quantity: 0
        }))
      );
    }
  }, [stockZoneInventoryBalances]);

  const hasAnyQuantity = quarantineAmounts.some(({ quantity }) => quantity > 0);

  return (
    <Page className="quarantine quarantine-unserialized action-page">
      <ActionTopBar action="Quarantine Unserialized" backPath="/" />
      <div className="action-page-body">
        <div
          className={['stock-zone', ...(!stockZoneId ? ['active'] : [])].join(' ')}
          onClick={() => setModalZoneType('Stock')}
        >
          <FiEdit />
          <h1>Stock Zone</h1>
          <h2 className={!stockZoneName ? 'zone-placeholder' : ''}>
            {stockZoneName || tapPrompt}
          </h2>
        </div>
        {!!stockZoneId && !stockZoneInventoryBalances.length && (
          <div className="empty-zone">Empty zone</div>
        )}
        {stockZoneInventoryBalances.map((zoneInventoryBalance) => {
          const { id, lot, stocked, product } = zoneInventoryBalance;
          const { quantity: quarantineQuantity } =
            quarantineAmounts.find(({ id: amountId }) => id === amountId) || [];
          return (
            <ProductInLocation
              key={id}
              product={product}
              lot={lot}
              quantity={stocked}
              productId={id}
              newQuantity={quarantineQuantity}
              actionType="Quarantine"
              setNewQuantity={(value) =>
                setQuarantineAmounts(
                  quarantineAmounts.map((amount) =>
                    id === amount.id
                      ? {
                          ...amount,
                          quantity: value
                            ? Math.min(parseInt(value), stocked)
                            : parseInt(value)
                        }
                      : amount
                  )
                )
              }
            />
          );
        })}
        <FiArrowDown className="quarantine-unserialized__arrow" aria-hidden />
        <div
          className={[
            'quarantine-zone',
            ...(!!stockZoneId && !quarantineZoneId ? ['active'] : [])
          ].join(' ')}
          onClick={() => !!stockZoneId && setModalZoneType('Quarantine')}
        >
          {!!stockZoneId && <FiEdit />}
          <h1>Quarantine Zone</h1>
          <h2 className={!quarantineZoneName ? 'zone-placeholder' : ''}>
            {quarantineZoneName || (stockZoneId ? tapPrompt : 'Select a Stock zone first')}
          </h2>
        </div>
        <Button
          type="primary"
          fullWidth
          className="quarantine-unserialized__submit"
          onClick={submitQuarantineUnserializedItems}
          disabled={!stockZoneId || !quarantineZoneId || !hasAnyQuantity}
        >
          Quarantine Unserialized
        </Button>
      </div>
      {quarantined && (
        <div className="result-panel open">
          <div className="result-message">
            <FiCheckCircle style={{ color: 'var(--green)' }} />
            <h1>Quarantined Unserialized Successfully</h1>
            <p>
              The following items have been sent to quarantine zone{' '}
              <b>{quarantineZoneName}</b>
            </p>

            {quarantineAmounts
              .filter(({ quantity }) => quantity > 0)
              .map(({ id, quantity }) => ({
                ...(stockZoneInventoryBalances.find(
                  ({ id: zoneInventoryBalanceId }) => id === zoneInventoryBalanceId
                ) || {}),
                quantity
              }))
              .map(({ id: balanceId, product, lot, quantity }) => (
                <div className="quarantined-products" key={balanceId}>
                  <WarehouseProductDisplay product={product} />
                  <KeyVal
                    items={[
                      {
                        key: 'Lot',
                        value: lot
                      },
                      {
                        key: 'Quantity',
                        value: quantity
                      }
                    ]}
                  />
                </div>
              ))}
          </div>
          <div className="result-actions">
            <Button type="primary" fullWidth onClick={() => navigate('/')}>
              Done
            </Button>
          </div>
        </div>
      )}
      <ZonePickerModal
        className="stock-zone-modal"
        open={!!modalZoneType}
        {...pickerProps(() => setModalZoneType(null))}
        title={`Select ${modalZoneType} zone:`}
        companyId={companyId}
        zoneTypes={isQuarantineModal ? ['quarantine'] : ['stock']}
        excludeZoneIds={
          isQuarantineModal && stockZoneId
            ? [stockZoneId]
            : modalZoneType === 'Stock' && quarantineZoneId
              ? [quarantineZoneId]
              : []
        }
        excludeMessage="That zone is already selected on the other side of this move."
        scanTitle={
          isQuarantineModal ? 'Scan quarantine zone' : 'Scan the stock zone to quarantine from'
        }
        scanHint={
          isQuarantineModal
            ? `Quarantining ${quarantiningUnits || 'stock'} from ${stockZoneName}. ` +
              'Scan the label on the quarantine zone it is going into.'
            : 'Quarantine unserialized: scan the label on the stock zone the items are coming ' +
              'out of. You choose the quantities next.'
        }
        onZoneChosen={({ id, display }) => {
          if (isQuarantineModal) {
            setQuarantineZoneId(id);
            setQuarantineZoneName(display);
          } else {
            setStockZoneId(id);
            setStockZoneName(display);
            getStockZoneInventoryBalances({ variables: { companyId, zoneId: id } });
          }
        }}
      />
    </Page>
  );
};

export default QuarantineUnserialized;
